import React from 'react';
import {
  Badge,
  EmptyState,
  FilterToolbar,
  FilterToolbarSearch,
  Flex,
  Heading,
  Spinner,
  Text,
  useToast,
} from '@chatsdet/ui';
import { Books } from '@phosphor-icons/react';
import {
  useContextDocuments,
  useDeleteContextDocument,
  useDownloadContextDocument,
  useUpdateContextDocument,
  type ContextDocument,
} from '../useContextLibrary';
import { ContextUploadZone } from '../components/ContextUploadZone';
import { DocumentCard } from '../components/DocumentCard';
import { fileTypeLabel } from '../fileTypeMeta';

/** Type filter chips, in display order. Only types present render as chips. */
const FILTER_TYPES = ['pdf', 'doc', 'image', 'csv', 'code', 'code-archive', 'link', 'paste'];

/**
 * Agent tools → Context Library: the standalone home for the org's shared
 * reference material. Add files / links / pasted text, describe each one, and
 * manage the whole pool. Descriptions are surfaced to the agents that read this
 * library while planning and testing.
 */
export function ContextLibraryPage() {
  const { data: documents, isLoading, isError } = useContextDocuments();
  const updateMutation = useUpdateContextDocument();
  const deleteMutation = useDeleteContextDocument();
  const downloadMutation = useDownloadContextDocument();
  const { toast } = useToast();

  const [search, setSearch] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState<string | null>(null);

  const docs = documents ?? [];

  const presentTypes = React.useMemo(
    () => FILTER_TYPES.filter((t) => docs.some((d) => d.fileType === t)),
    [docs]
  );

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (typeFilter && d.fileType !== typeFilter) return false;
      if (!q) return true;
      return (
        d.fileName.toLowerCase().includes(q) ||
        (d.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [docs, search, typeFilter]);

  const handleDownload = React.useCallback(
    (id: string) => {
      downloadMutation.mutate(id, {
        onSuccess: (url) => window.open(url, '_blank', 'noopener,noreferrer'),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to open document'),
      });
    },
    [downloadMutation, toast]
  );

  const handleSaveDescription = React.useCallback(
    (id: string, description: string | null) => {
      updateMutation.mutate(
        { id, description },
        {
          onError: (err) =>
            toast.error(err instanceof Error ? err.message : 'Failed to save description'),
        }
      );
    },
    [toast, updateMutation]
  );

  const handleDelete = React.useCallback(
    (id: string) => {
      deleteMutation.mutate(id, {
        onSuccess: () => toast.success('Document deleted'),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to delete document'),
      });
    },
    [deleteMutation, toast]
  );

  const handleAdded = React.useCallback(
    (added: ContextDocument[]) => {
      if (added.length > 0) {
        toast.success(added.length === 1 ? 'Added to library' : `Added ${added.length} documents`);
      }
    },
    [toast]
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-6">
      <div className="space-y-1">
        <Flex align="center" gap="sm">
          <Books size={22} weight="duotone" className="text-primary" />
          <Heading level={1} className="text-xl">
            Context Library
          </Heading>
        </Flex>
        <Text size="sm" className="text-muted-foreground">
          Shared reference material your agents read while planning and testing — specs, designs,
          runbooks, CSVs, links, and notes. Describe each one so the agent knows how to use it.
        </Text>
      </div>

      <ContextUploadZone onAdded={handleAdded} allowPaste />

      <div className="space-y-3">
        <Flex align="center" justify="between" gap="sm" className="flex-wrap">
          <Flex align="center" gap="sm" className="flex-wrap">
            <button
              type="button"
              onClick={() => setTypeFilter(null)}
              className="appearance-none border-0 bg-transparent p-0"
            >
              <Badge variant={typeFilter === null ? 'default' : 'outline'} size="sm">
                All ({docs.length})
              </Badge>
            </button>
            {presentTypes.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setTypeFilter((cur) => (cur === type ? null : type))}
                className="appearance-none border-0 bg-transparent p-0"
              >
                <Badge variant={typeFilter === type ? 'default' : 'outline'} size="sm">
                  {fileTypeLabel(type)}
                </Badge>
              </button>
            ))}
          </Flex>
          <FilterToolbar>
            <FilterToolbarSearch
              aria-label="Search context library"
              value={search}
              onChange={setSearch}
              placeholder="Search name or description…"
            />
          </FilterToolbar>
        </Flex>

        {isLoading ? (
          <Flex align="center" justify="center" className="py-16">
            <Spinner />
          </Flex>
        ) : isError ? (
          <EmptyState
            icon={<Books size={24} />}
            title="Couldn't load your library"
            description="Something went wrong fetching documents. Try refreshing."
          />
        ) : docs.length === 0 ? (
          <EmptyState
            icon={<Books size={24} />}
            title="No context yet"
            description="Add files, links, or pasted notes above. They become shared reading material for every agent in your org."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Books size={24} />}
            title="No matches"
            description="No documents match the current filter."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((doc) => (
              <DocumentCard
                key={doc.id}
                doc={doc}
                onDownload={handleDownload}
                onSaveDescription={handleSaveDescription}
                onDelete={handleDelete}
                isSavingDescription={updateMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
